import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { BankAccount, BankAgencies, Currency } from '../../models/bank';

export type BankAccountFormT = {
  [P in keyof BankAccount]: FormControl<BankAccount[P] | null>;
};

/**
 * Represents a form for creating a bank account.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'edt-bank-account-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxMaskDirective,
  ],
  templateUrl: './bank-account-form.component.html',
  styleUrls: ['./bank-account-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideNgxMask()],
})
export class BankAccountFormComponent {
  /**
   * An array of bank agencies that can be selected in the form.
   */
  @Input() bankAgencies: BankAgencies[] = [
    'BANDEC',
    'BPA',
    'METROPOLITANO',
    'BFI',
  ];

  /**
   * An array of currencies that can be selected in the form.
   */
  @Input() currencies: Array<Currency> = ['CUP', 'USD', 'CL'];

  /**
   * The form group that holds the form controls for the bank account fields.
   * It is initialized with default values and validators using the `FormBuilder` service.
   */
  @Input() bankAccountForm: FormGroup<BankAccountFormT> = this.fb.group({
    number: [
      '',
      {
        validators: [Validators.required, Validators.pattern('^[0-9]{16}$')],
      },
    ],
    bank_agency: ['BANDEC' as BankAgencies, [Validators.required]],
    bank_branch: ['', [Validators.required, Validators.maxLength(255)]],
    currency: ['CUP' as Currency, [Validators.required]],
  });

  constructor(private fb: FormBuilder) {}

  /**
   * Returns the `FormControl` instance for the account number field.
   */
  get number() {
    return this.bankAccountForm.controls['number'];
  }

  /**
   * Returns the `FormControl` instance for the bank branch field.
   */
  get bankBranch() {
    return this.bankAccountForm.controls['bank_branch'];
  }
}
